import React, { useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";

import { apiFetch, getCurrentUser } from "../../Helper/Api";
import { t } from "../../Helper/i18n";
import Header from "../../components/Header/Header";
import { Modal, Confirm } from "../../components/UI/Overlays";
import { EmptyState, TableWait, Chip } from "../../components/UI/Feedback";
import { Toolbar } from "../../components/UI/Controls";

const properties = require("../../properties.json");

const KIND = { ACTIVE: "ok", TRIAL: "wait", PENDING: "wait", EXPIRED: "off", CANCELED: "bad" };
const date = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

/** The members of the coach and their memberships: requests wait for activation, a running one can be extended or ended. */
export default function Members() {
  const toast = useRef(null);
  const [rows, setRows] = useState(null);
  const [plans, setPlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [filter, setFilter] = useState("");
  const [add, setAdd] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ending, setEnding] = useState(null);

  const load = () =>
    apiFetch("/plans/members")
      .then(setRows)
      .catch(() => setRows([]));

  useEffect(() => {
    document.title = properties.site_name + " | " + t("gym.members");
    load();
    apiFetch("/plans").then(setPlans).catch(() => {});
    apiFetch("/students", { query: { branchId: getCurrentUser()?.branchId } }).then(setStudents).catch(() => {});
  }, []);

  const say = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === "error" ? t("common.alert") : t("common.done"), detail });
  const fail = (e) => say("error", (e && e.message) || t("common.failed"));

  const activate = (planId, studentProfileId, months) => {
    setBusy(true);
    return apiFetch(`/plans/${planId}/members`, { method: "POST", body: { studentProfileId, months: months || null } })
      .then(() => {
        setAdd(null);
        say("success", t("gym.members.activated"));
        return load();
      })
      .catch(fail)
      .finally(() => setBusy(false));
  };

  const cancel = (m) =>
    apiFetch(`/plans/members/${m.id}`, { method: "PUT", body: { cancel: true } })
      .then(() => {
        setEnding(null);
        return load();
      })
      .catch((e) => {
        setEnding(null);
        fail(e);
      });

  const visible = (rows || []).filter((r) => !filter || `${r.studentName} ${r.mobile} ${r.planName}`.toLowerCase().includes(filter.toLowerCase()));
  const running = (r) => r.status === "ACTIVE" || r.status === "TRIAL";

  return (
    <>
      <Header pageName={t("gym.members")} action={{ label: t("gym.members.add"), icon: "pi pi-user-plus", onClick: () => setAdd({ planId: plans[0] ? plans[0].id : null, studentId: null, months: "" }) }} />
      <Toast ref={toast} />
      <Confirm
        visible={!!ending}
        onHide={() => setEnding(null)}
        name={ending ? ending.studentName : ""}
        title={t("gym.members.endTitle")}
        note={t("gym.members.endNote")}
        tone="warn"
        onConfirm={() => cancel(ending)}
      />
      <Modal
        visible={!!add}
        onHide={() => setAdd(null)}
        title={t("gym.members.add")}
        footer={
          <button type="button" className="ui-btn" disabled={busy || !add || !add.planId || !add.studentId} onClick={() => activate(add.planId, add.studentId, add.months ? Number(add.months) : null)}>
            {busy ? <i className="pi pi-spin pi-spinner"></i> : <i className="pi pi-check"></i>} {t("gym.members.activate")}
          </button>
        }
      >
        {add ? (
          <div className="ui-modal-form">
            <label className="ui-filter">
              <span>{t("gym.members.member")}</span>
              <Dropdown filter options={students.map((s) => ({ label: `${s.fullName} ${s.mobile || ""}`, value: s.id }))} value={add.studentId} onChange={(e) => setAdd({ ...add, studentId: e.value })} placeholder={t("gym.members.pick")} />
            </label>
            <label className="ui-filter">
              <span>{t("gym.members.plan")}</span>
              <Dropdown options={plans.map((p) => ({ label: p.name, value: p.id }))} value={add.planId} onChange={(e) => setAdd({ ...add, planId: e.value })} />
            </label>
            <label className="ui-filter">
              <span>{t("gym.members.months")}</span>
              <InputText type="number" min="1" max="36" value={add.months} placeholder={t("gym.members.monthsHint")} onChange={(e) => setAdd({ ...add, months: e.target.value })} />
            </label>
          </div>
        ) : null}
      </Modal>

      <div className="content">
        <div className="container">
          <div className="card">
            <Card>
              <Toolbar value={filter} onSearch={setFilter} placeholder={t("gym.members.search")} count={rows ? `${visible.length}` : null} />
              <DataTable
                value={visible}
                paginator
                rows={10}
                className="p-datatable-responsive-demo p-datatable-gridlines p-datatable-striped"
                emptyMessage={rows === null ? <TableWait /> : <EmptyState icon="pi pi-users" title={t("gym.members.emptyTitle")} hint={t("gym.members.emptyText")} />}
              >
                <Column field="studentName" header={t("gym.members.member")} />
                <Column field="mobile" header={t("gym.members.mobile")} />
                <Column field="planName" header={t("gym.members.plan")} />
                <Column header={t("gym.members.status")} body={(r) => <Chip kind={KIND[r.status] || "off"}>{t("gym.status." + r.status)}</Chip>} />
                <Column header={t("gym.members.endsAt")} body={(r) => date(r.endsAt)} />
                <Column
                  header=""
                  body={(r) => (
                    <div className="p-d-flex">
                      {r.status === "PENDING" || r.status === "TRIAL" ? (
                        <Button icon="pi pi-check" tooltip={t("gym.members.activate")} className="p-button-rounded p-button-success p-ml-2" onClick={() => activate(r.planId, r.studentProfileId)} />
                      ) : null}
                      {r.status === "ACTIVE" || r.status === "EXPIRED" || r.status === "CANCELED" ? (
                        <Button icon="pi pi-plus" tooltip={t("gym.members.extend")} className="p-button-rounded p-button-secondary p-ml-2" onClick={() => activate(r.planId, r.studentProfileId)} />
                      ) : null}
                      {running(r) || r.status === "PENDING" ? <Button icon="pi pi-ban" tooltip={t("gym.members.end")} className="p-button-rounded p-button-danger" onClick={() => setEnding(r)} /> : null}
                    </div>
                  )}
                />
              </DataTable>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
